import React, { useMemo } from 'react';
import styles from '../AIFeatures.module.scss';
import { STATUS } from '../constants';
import classNames from 'classnames';
import { Skeleton } from '@material-ui/lab';
import { CircularProgress } from '@material-ui/core';
import { useAutoFillStore } from 'features/roleplay/Components/Autorating/AutoFill.store';
import sparkle from 'assets/icons/sparkle.svg';
const AIFeedback = ({
  ai_feedback = '',
  ai_feedback_status,
  assessment = false,
  onlyView = false,
  interactiveRoleplay = false,
  error_msg = '',
}) => {
  const feedbackAI = useAutoFillStore((state) => state?.feedbackAI);
  const title = useMemo(() => {
    if (ai_feedback_status === STATUS.NOT_SUPPORTED) return 'AI-Powered Feedback is not available.';
    return '';
  }, [ai_feedback_status]);

  const text = useMemo(() => {
    if (ai_feedback_status === STATUS.IN_PROGRESS)
      return "AI Rating in Progress… We're analyzing your content to ensure you receive the best feedback.";
    if (ai_feedback_status === STATUS.NOT_SUPPORTED)
      return feedbackAI !== ''
        ? feedbackAI
        : "This could happen if the video is muted or doesn't have any sound.";
    if (ai_feedback_status === STATUS.UNRATED)
      return feedbackAI !== '' ? feedbackAI : 'AI-Powered Feedback is not available.';
    return ai_feedback;
  }, [ai_feedback_status, ai_feedback, feedbackAI]);

  const skeletons = useMemo(() => {
    if (ai_feedback_status !== STATUS.IN_PROGRESS) return [];
    const widths = [30, 35, 40, 50, 60, 70];
    const aux = [];
    for (let index = 0; index < 19; index++)
      aux.push(widths[Math.floor(Math.random() * widths.length)]);
    return aux;
  }, [ai_feedback_status]);

  const textAssessment = useMemo(() => {
    if ([STATUS.IN_PROGRESS, STATUS.QUEUED].includes(ai_feedback_status))
      return "AI Rating in Progress… We're analyzing your content to ensure you receive the best feedback.";

    if (ai_feedback_status === STATUS.NOT_SUPPORTED || ai_feedback_status === STATUS.UNRATED)
      return `Please try later. ${error_msg}`;
    return ai_feedback;
  }, [ai_feedback_status, ai_feedback, error_msg]);

  const titleAssessment = useMemo(() => {
    if (ai_feedback_status === STATUS.NOT_SUPPORTED) return 'AI-Powered Feedback is not available.';
    if (ai_feedback_status === STATUS.UNRATED) return 'AI-Powered Feedback could not be completed';
    return '';
  }, [ai_feedback_status]);

  if (ai_feedback_status === STATUS.NOT_FOUND || ai_feedback_status === STATUS.NO_SCENARIOS)
    return (
      <div className={styles.error}>
        <p className={styles.textError}>AI-Powered Feedback is not available.</p>
      </div>
    );

  if (assessment)
    return (
      <div
        className={classNames([
          ai_feedback_status === STATUS.SUCCESS && styles.success1,
          ai_feedback_status === STATUS.IN_PROGRESS && styles.loading1,
          ai_feedback_status === STATUS.QUEUED && styles.loading1,
          ai_feedback_status === STATUS.NOT_SUPPORTED && styles.error,
          ai_feedback_status === STATUS.UNRATED && styles.error,
          !ai_feedback_status && styles.withoutStatus,
        ])}
      >
        {titleAssessment !== '' && <p className={styles.titleError}>{titleAssessment}</p>}

        {ai_feedback_status === STATUS.IN_PROGRESS || ai_feedback_status === STATUS.QUEUED ? (
          <div className={styles.loadingFeedback}>
            <CircularProgress size={15} disableShrink variant="indeterminate" />
            <p className={styles.loadingText}>{textAssessment}</p>
          </div>
        ) : (
          <p
            className={
              ai_feedback_status === STATUS.NOT_SUPPORTED || ai_feedback_status === STATUS.UNRATED
                ? styles.textError
                : styles.textFeedback
            }
          >
            {textAssessment}
          </p>
        )}
      </div>
    );

  if (assessment && onlyView)
    return (
      <div className={styles.success1}>
        <p className={styles.textFeedback}>{ai_feedback}</p>
      </div>
    );

  if (interactiveRoleplay)
    return (
      <div
        className={classNames([
          ai_feedback_status === STATUS.SUCCESS && styles.overallFeedback,
          ai_feedback_status === STATUS.IN_PROGRESS && styles.loading1,
          ai_feedback_status === STATUS.NOT_SUPPORTED && styles.error,
        ])}
      >
        {titleAssessment !== '' && <p className={styles.titleError}>{titleAssessment}</p>}
        {ai_feedback_status === STATUS.IN_PROGRESS || ai_feedback_status === STATUS.QUEUED ? (
          <div className={styles.loadingFeedback}>
            <CircularProgress size={15} disableShrink variant="indeterminate" />
            <p className={styles.loadingText}>{textAssessment}</p>
          </div>
        ) : (
          <p
            className={
              ai_feedback_status === STATUS.NOT_SUPPORTED || ai_feedback_status === STATUS.UNRATED
                ? styles.textError
                : styles.textFeedback
            }
          >
            <img src={sparkle} alt="sparkle" style={{ marginRight: '0.5rem' }} />
            {textAssessment}
          </p>
        )}
      </div>
    );

  return (
    <div
      className={classNames([
        ai_feedback_status === STATUS.SUCCESS && styles.success1,
        ai_feedback_status === STATUS.IN_PROGRESS && styles.loading1,
        ai_feedback_status === STATUS.NOT_SUPPORTED && styles.error,
        ai_feedback_status === STATUS.UNRATED && styles.error,
      ])}
    >
      {title !== '' && <p className={styles.titleError}>{title}</p>}

      {ai_feedback_status === STATUS.IN_PROGRESS ? (
        <div className={styles.loadingFeedback}>
          <CircularProgress size={15} disableShrink variant="indeterminate" />
          <p className={styles.loadingText}>{text}</p>
        </div>
      ) : (
        <p
          className={
            ai_feedback_status === STATUS.NOT_SUPPORTED || ai_feedback_status === STATUS.UNRATED
              ? styles.textError
              : styles.textFeedback
          }
        >
          {text}
        </p>
      )}
      {ai_feedback_status === STATUS.IN_PROGRESS && (
        <div className={styles.loadingContainer}>
          {skeletons.map((width, i) => (
            <Skeleton key={i} variant="text" width={`${width}px`} height={12} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AIFeedback;
